import React, { useContext, useState, useEffect } from "react";
import { Form as FinalForm, Field } from "react-final-form";
import {
  Form,
  Button,
  Dropdown,
  Label,
  Grid,
} from "semantic-ui-react";
import { RootStoreContext } from "../../../../app/stores/rootStore";
import TextInput from "../../../../app/common/form/TextInput";
import {
  combineValidators,
  composeValidators,
  isRequired,
  hasLengthLessThan,
  isNumeric,
  hasLengthGreaterThan,
  matchesPattern,
} from "revalidate";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";

const validate = combineValidators({
  companyID: composeValidators(
    isRequired({ message: "El NIT es obligatorio" }),
    isNumeric({ message: "El NIT debe ser numerico" }),
    hasLengthGreaterThan(6)({
      message: "El NIT debe tener al menos 7 caracteres",
    }),
    hasLengthLessThan(11)({
      message: "El NIT debe tener máximo 10 caracteres",
    })
  )(),
  digitCheck: composeValidators(
    isRequired({ message: "El DV es obligatorio" }),
    isNumeric({ message: "El DV debe ser numerico" }),
    hasLengthLessThan(2)({
      message: "El DV debe tener máximo 1 caracter",
    })
  )(),
  registrationName: composeValidators(
    isRequired({ message: "El Nombre o Razón Social es obligatorio" }),
    hasLengthGreaterThan(1)({
      message: "El Nombre o Razón Social debe tener al menos 2 caracteres",
    }),
    hasLengthLessThan(257)({
      message: "El Nombre o Razón Social debe tener máximo 256 caracteres",
    }),
    matchesPattern(/^[A-zÀ-ÿ0-9 '.,;:&_@-]*$/)({
      message:
        "Nombre no válido. Contiene caracteres especiales diferente a: ' . , ; : & _ @",
    })
  )(),
  name: composeValidators(
    isRequired({ message: "El Nombre Comercial es obligatorio" }),
    hasLengthGreaterThan(1)({
      message: "El Nombre Comercial debe tener al menos 2 caracteres",
    }),
    hasLengthLessThan(257)({
      message: "El Nombre Comercial debe tener máximo 256 caracteres",
    }),
    matchesPattern(/^[A-zÀ-ÿ0-9 '.,;:&_@-]*$/)({
      message:
        "Nombre Comercial no válido. Contiene caracteres especiales diferente a: ' . , ; : & _ @",
    })
  )(),
  "contact.phone": composeValidators(
    isRequired({ message: "El Teléfono es obligatorio" }),
    matchesPattern(/^[a-zA-Z0-9.()-\\]*$/)({
      message:
        "Teléfono no válido. Contiene espacios o caracteres especiales diferente a: ( ) - . \\",
    }),
    hasLengthGreaterThan(6)({
      message: "El Teléfono debe tener al menos 7 caracteres",
    }),
    hasLengthLessThan(33)({
      message: "El Teléfono debe tener máximo 32 caracteres",
    })
  )(),
  "address.line": composeValidators(
    isRequired({ message: "La Dirección es obligatoria" }),
    hasLengthGreaterThan(6)({
      message: "La Dirección debe tener al menos 7 caracteres",
    }),
    hasLengthLessThan(257)({
      message: "La Dirección debe tener máximo 256 caracteres",
    }),
    matchesPattern(/^[a-zA-Z0-9,_#-\s\\.]*$/)({
      message:
        "Direccion no válida. Contiene espacios o caracteres especiales diferente a: # , - _ . \\",
    })
  )(),
  "address.postalZone": composeValidators(
    hasLengthLessThan(7)({
      message: "El Código postal debe tener máximo 6 caracteres",
    }),
    hasLengthGreaterThan(5)({
      message: "El Código postal debe tener al menos 6 caracteres",
    }),
    isNumeric({ message: "El Código postal debe ser numerico" })
  )(),
  "address.countrySubentityCode": composeValidators(
    isRequired({ message: "Debe seleccionar un Departamento" })
  )(),
  "address.cityCode": composeValidators(
    isRequired({ message: "Debe seleccionar una Ciudad" })
  )(),  
  "notificationEmail": composeValidators(
    isRequired({ message: "El Email es obligatorio" }),
    matchesPattern(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/)({
      message: "Por favor digite un Email valido",
    }),
    hasLengthLessThan(257)({
      message: "El Email debe tener máximo 256 caracteres",
    }),
    hasLengthGreaterThan(5)({
      message: "El Email debe tener al menos 6 caracteres",
    })
  )(),
  "contact.email": composeValidators(
    isRequired({ message: "El Email es obligatorio" }),
    matchesPattern(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/)({
      message: "Por favor digite un Email valido",
    }),
    hasLengthLessThan(257)({
      message: "El Email debe tener máximo 256 caracteres",
    }),
    hasLengthGreaterThan(5)({
      message: "El Email debe tener al menos 6 caracteres",
    })
  )(),
});

const EditCompanyForm = () => {
  //variables para uso la informacion del userStore
  const rootStore = useContext(RootStoreContext);
  const {
    getCities,
    cities,
    getCountrySubentities,
    countrySubentities,
  } = rootStore.listOfValours;
  const { supplier } = rootStore.companyStore;
  const { updateSupplier, btEditSupplier } = rootStore.companyStore;

  useEffect(() => {
    getCountrySubentities();
    if (supplier?.address.countrySubentityCode != null) {
      getCities(supplier?.address.countrySubentityCode);
    }
  }, [getCountrySubentities, getCities, supplier]);

  const [companyForm] = useState({
    companyID: supplier?.companyID,
    digitCheck: supplier?.digitCheck,
    registrationName: supplier?.registrationName,
    contact: supplier?.contact,
    notificationEmail: supplier?.notificationEmail,
    name: supplier?.name,
    sendState: supplier?.sendState || 0,
    address: supplier?.address,
    economicActivity: supplier?.economicActivity,
  });

  const handleFinalFormSubmit = (values: any) => {
    if (
      values.companyID !== companyForm.companyID ||
      values.digitCheck !== companyForm.digitCheck ||
      values.registrationName !== companyForm.registrationName ||
      values.name !== companyForm.name ||
      values.address !== companyForm.address ||
      values.contact !== companyForm.contact || 
      values.notificationEmail !== companyForm.notificationEmail ||
      values.economicActivity !== companyForm.economicActivity
    ) {
      companyForm.companyID = values.companyID.trim();
      companyForm.digitCheck = values.digitCheck.trim();
      companyForm.registrationName = values.registrationName.trim();
      companyForm.name = values.name.trim();
      companyForm.notificationEmail = values.notificationEmail.trim();
      companyForm.address = values.address;
      companyForm.contact = values.contact;
      companyForm.economicActivity = values.economicActivity == null? null : values.economicActivity.trim();
      updateSupplier(companyForm);
    } else toast.error("No ha hecho ningún cambio");
  };

  const getCitieshandle = (idDepartamento: any) => {
    getCities(idDepartamento);
  };
  return (
    <div className="tabContentStyleSupplier">
      <FinalForm
        validate={validate}
        initialValues={companyForm}
        onSubmit={handleFinalFormSubmit}
        render={({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Grid relaxed doubling columns={2}>
              <Grid.Row>
                <Grid.Column>
                  <label>Nombre o Razón Social del emisor</label>
                  <p className="readonlyfield">
                    {companyForm.registrationName}
                  </p>
                </Grid.Column>
                <Grid.Column>
                  <label>Tipo de identificador fiscal</label>
                  <p className="readonlyfield">31-NIT</p>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <label>Nombre Comercial</label>
                  <Field
                    name="name"
                    value={companyForm.name}
                    component={TextInput}
                  />
                </Grid.Column>
                <Grid.Column>
                  <label>Código dentificador del país</label>
                  <p className="readonlyfield">CO-Colombia</p>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <label>NIT del emisor</label>
                  <p className="readonlyfield">{companyForm.companyID}</p>
                </Grid.Column>
                <Grid.Column>
                  <label>Departamento</label>
                  <Field name="address.countrySubentityCode">
                    {(props) => (
                      <Form.Field
                        error={props.meta.touched && !!props.meta.error}
                      >
                        <Dropdown
                          name={props.input.name}
                          fluid
                          selection
                          search={true}
                          onChange={(e, data) => (
                            props.input.onChange(data.value),
                            getCitieshandle(data.value)
                          )}
                          defaultValue={
                            companyForm.address?.countrySubentityCode
                          }
                          options={countrySubentities}
                        />
                        {props.meta.touched && props.meta.error && (
                          <Label basic color="red">
                            {props.meta.error}
                          </Label>
                        )}
                      </Form.Field>
                    )}
                  </Field>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <label>DV del NIT del emisor</label>
                  <Field
                    name="digitCheck"
                    value={companyForm.digitCheck}
                    component={TextInput}
                  />
                </Grid.Column>
                <Grid.Column>
                  <label>Ciudad</label>
                  <Field name="address.cityCode">
                    {(props) => (
                      <Form.Field
                        error={props.meta.touched && !!props.meta.error}
                      >
                        <Dropdown
                          name={props.input.name}
                          fluid
                          selection
                          search={true}
                          onChange={(e, data) =>
                            props.input.onChange(data.value)
                          }
                          defaultValue={companyForm.address?.cityCode}
                          options={cities}
                        />
                        {props.meta.touched && props.meta.error && (
                          <Label basic color="red">
                            {props.meta.error}
                          </Label>
                        )}
                      </Form.Field>
                    )}
                  </Field>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <label>Dirección</label>
                  <Field
                    name="address.line"
                    value={companyForm.address?.line}
                    component={TextInput}
                  />
                </Grid.Column>
                <Grid.Column>
                  <label>Código postal</label>
                  <Field
                    name="address.postalZone"
                    value={companyForm.address?.postalZone}
                    component={TextInput}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <label>Email</label>
                  <Field
                    name="contact.email"
                    value={companyForm.contact.email}
                    component={TextInput}
                  />
                </Grid.Column>
                <Grid.Column>
                <label>Email para notificaciones del sistema</label>
                  <Field
                    name="notificationEmail"
                    value={companyForm.notificationEmail}
                    component={TextInput}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <label>Teléfono</label>
                  <Field
                    name="contact.phone"
                    value={companyForm.contact.phone}
                    component={TextInput}
                  />
                </Grid.Column>
                <Grid.Column>
                <label>Actividad económica</label>
                  <Field
                    name="economicActivity"
                    value={companyForm.economicActivity}
                    component={TextInput}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <br />
            <Button
              loading={btEditSupplier}
              disabled={btEditSupplier}
              className="ui primary button"
              color="blue"
              type="submit"
              content="Guardar"
            />
          </Form>
        )}
      />
    </div>
  );
};

export default observer(EditCompanyForm);
